import React, { useState, useRef, useEffect } from "react";
import { IoInformationCircleOutline } from "react-icons/io5";
import { BsFileEarmarkText, BsFiletypePdf } from "react-icons/bs";
import { AiOutlineGlobal } from "react-icons/ai";

import ActiveTool from "../Sandbox/ActiveTool";
import { FaCheck, FaChevronDown } from "react-icons/fa";
import DemoChat from "../Sandbox/DemoChat";

const dataSources = [
  {
    key: "website",
    label: "Website",
    icon: <AiOutlineGlobal size={24} />,
  },
  {
    key: "pdf",
    label: "PDF",
    icon: <BsFiletypePdf size={24} />,
  },
  {
    key: "text",
    label: "Text",
    icon: <BsFileEarmarkText size={24} />,
  },
];

const Sandbox = ({ dispatch, state, handelTrain, trainLoad }) => {
  const [showTooltip, setShowTooltip] = useState(false);
  const [activeTool, setActiveTool] = useState("website");
  const [isOpen, setIsOpen] = useState(false);

  const dropdownRef = useRef(null);

  const sandboxInfo = useRef(null);

  useEffect(() => {
    function handleClickOutside(event) {
      if (sandboxInfo.current && !sandboxInfo.current.contains(event.target)) {
        setShowTooltip(false);
      }
    }

    if (showTooltip) {
      document.addEventListener("mousedown", handleClickOutside);
    } else {
      document.removeEventListener("mousedown", handleClickOutside);
    }

    // Cleanup on unmount
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showTooltip]);

  useEffect(() => {
    const handleClickOutsider = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutsider);
    return () => {
      document.removeEventListener("mousedown", handleClickOutsider);
    };
  }, []);

  const handleOptionClick = (key) => {
    setActiveTool(key);
    setIsOpen(false);
  };

  const selectedItem = dataSources.find((item) => item.key === activeTool);

  return (
    <div className="flex flex-1 gap-4 relative p-3 sm:p-6 py-4 ">
      <div className="flex-col flex w-full">
        <div className="flex w-full justify-between gap-1 my-3">
          <div className="flex gap-1 items-end">
            <h1 className="text-2xl font-semibold text-gray-600">
              Sandbox Environment
            </h1>

            <div ref={sandboxInfo} className="relative">
              <IoInformationCircleOutline
                size={24}
                className="text-gray-500 cursor-pointer"
                onClick={() => setShowTooltip((prev) => !prev)}
                onMouseEnter={() => setShowTooltip(true)}
              />
              {showTooltip && (
                <div
                  className="absolute z-10 w-64 p-3 bg-white border border-gray-300 rounded shadow-lg text-gray-700 text-sm"
                  style={{
                    top: "110%",
                    left: "50%",
                    transform: "translateX(-50%)",
                  }}>
                  This is a sandbox environment for testing and development. It
                  allows you to experiment with your bots without affecting the
                  production environment.
                </div>
              )}
            </div>
          </div>

          <button
            disabled={trainLoad}
            onClick={handelTrain}
            className={`text-white bg-gray-900 hover:bg-black px-10 py-3 hidden md:block rounded-lg shadow-md ${
              trainLoad ? "cursor-not-allowed" : "cursor-pointer"
            }`}>
            {trainLoad ? "Training..." : "Retrain Bot"}
          </button>
        </div>
        <div className="flex flex-col h-full md:flex-row gap-3 relative">
          <div className="flex flex-col w-full md:w-3/4 rounded-lg relative">
            <div className="flex flex-col md:flex-row flex-1 gap-4 mt-3">
              {/* Mobile: Select Dropdown */}
              <div className="relative w-full md:hidden" ref={dropdownRef}>
                {/* Trigger Button */}
                <button
                  type="button"
                  aria-haspopup="listbox"
                  aria-expanded={isOpen}
                  onClick={() => setIsOpen(!isOpen)}
                  className="w-full flex items-center justify-between rounded-lg border border-gray-300 bg-white px-4 py-3 text-left text-base shadow-sm focus:outline-none focus:ring-2 focus:ring-violet-500">
                  <div className="flex items-center space-x-3">
                    <span className="text-gray-500">{selectedItem?.icon}</span>
                    <span className="text-gray-900 font-medium">
                      {selectedItem?.label}
                    </span>
                  </div>
                  <FaChevronDown
                    className={`h-4 w-4 text-gray-400 transform transition-transform duration-200 ${
                      isOpen ? "rotate-180" : ""
                    }`}
                  />
                </button>

                {/* Dropdown Panel */}
                {isOpen && (
                  <div
                    role="listbox"
                    className="absolute mt-2 w-full rounded-md bg-white shadow-lg z-10 border border-gray-200 animate-fade-in-sm">
                    <ul className="max-h-60 overflow-auto rounded-md py-1 focus:outline-none">
                      {dataSources.map((item) => (
                        <li
                          key={item.key}
                          role="option"
                          aria-selected={item.key === activeTool}
                          onClick={() => handleOptionClick(item.key)}
                          className="relative cursor-pointer select-none py-2 px-4 text-gray-900 hover:bg-violet-50 hover:text-violet-900 transition-colors flex items-center space-x-3">
                          <span className="text-violet-600 opacity-75">
                            {item.icon}
                          </span>
                          <span
                            className={`block truncate ${
                              item.key === activeTool
                                ? "font-semibold"
                                : "font-normal"
                            }`}>
                            {item.label}
                          </span>
                          {item.key === activeTool && (
                            <span className="absolute inset-y-0 right-0 flex items-center pr-4 text-violet-600">
                              <FaCheck className="h-4 w-4" />
                            </span>
                          )}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>

              {/* Desktop: Sidebar List */}
              <ul className="hidden md:block space-y-1 mt-2">
                {dataSources.map(({ icon, label, key }) => (
                  <li
                    key={key}
                    onClick={() => setActiveTool(key)}
                    className={`group flex items-center gap-x-4 rounded-md p-2 font-semibold leading-6 transition cursor-pointer ${
                      activeTool === key ? "bg-violet-50 text-violet-700" : ""
                    }`}>
                    <span className="group-hover:text-violet-600 h-5 w-5 shrink-0">
                      {icon}
                    </span>
                    <span className="group-hover:text-violet-600">{label}</span>
                  </li>
                ))}
              </ul>

              <div className="flex flex-1 h-fit w-full gap-2  ">
                <ActiveTool
                  dispatch={dispatch}
                  state={state}
                  activeTool={activeTool}
                  IoInformationCircleOutline={IoInformationCircleOutline}
                  handelTrain={handelTrain}
                />
              </div>
            </div>
          </div>
          <DemoChat state={state} />
        </div>
      </div>
    </div>
  );
};

export default Sandbox;
